import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import cn from "classnames";
import LearnShell, { useLearnAccess } from "./LearnShell";
import CourseRow from "./CourseRow";
import { fetchCourse, saveProgress, saveNote, toggleSave, formatClock, formatDuration, countLabel } from "./learnApi";

const PROGRESS_INTERVAL_MS = 10000;
const AUTO_ADVANCE_SECONDS = 6;

const WatchBody = () => {
  const { slug, position } = useParams();
  const navigate = useNavigate();
  const access = useLearnAccess();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("lessons");
  const [note, setNote] = useState("");
  const [noteState, setNoteState] = useState("idle"); // idle | saving | saved
  const [endedLessonId, setEndedLessonId] = useState(null);
  const [countdown, setCountdown] = useState(AUTO_ADVANCE_SECONDS);
  const [copied, setCopied] = useState(false);
  const videoRef = useRef(null);
  const lastSentRef = useRef(0);
  const noteTimer = useRef(null);

  const pos = Number(position) || 1;

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    fetchCourse(slug)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [slug]);

  const course = data?.course;
  const lessons = useMemo(() => data?.lessons || [], [data]);
  const lesson = useMemo(() => lessons.find((l) => Number(l.position) === pos), [lessons, pos]);
  const nextLesson = useMemo(() => lessons.find((l) => Number(l.position) === pos + 1), [lessons, pos]);
  const prevLesson = useMemo(() => lessons.find((l) => Number(l.position) === pos - 1), [lessons, pos]);
  const ended = !!lesson && endedLessonId === lesson.id;

  // Bad position, or a package lesson the viewer hasn't unlocked → course landing.
  useEffect(() => {
    if (data && (!lesson || lesson.locked || data.course?.locked)) navigate(`/learn/${slug}`, { replace: true });
  }, [data, lesson, slug, navigate]);

  // Package-only lessons stream through the signed proxy; member lessons use
  // their direct URL. Watermark + no-download apply to the former.
  const isPackage = course?.access === "package";
  const playbackSrc = lesson?.stream_url || lesson?.video_url || "";
  const hasVideo = Boolean(playbackSrc) || Boolean(lesson?.has_video);

  // Reset per-lesson UI state.
  useEffect(() => {
    setCountdown(AUTO_ADVANCE_SECONDS);
    setNote(lesson?.note || "");
    setNoteState("idle");
    lastSentRef.current = 0;
    window.scrollTo(0, 0);
  }, [lesson?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchLesson = useCallback((lessonId, patch) => {
    setData((d) => {
      if (!d) return d;
      const nextLessons = d.lessons.map((l) => (l.id === lessonId ? { ...l, ...patch } : l));
      const completed = nextLessons.filter((l) => l.completed).length;
      return { ...d, lessons: nextLessons, course: { ...d.course, completed_count: completed } };
    });
  }, []);

  const sendProgress = useCallback((seconds, completed = false) => {
    if (!lesson) return;
    const s = Math.floor(seconds);
    saveProgress(lesson.id, s, completed).catch(() => {});
    patchLesson(lesson.id, { seconds_watched: Math.max(lesson.seconds_watched || 0, s), ...(completed ? { completed: true } : {}) });
  }, [lesson, patchLesson]);

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v || !lesson) return;
    const resume = Number(lesson.seconds_watched) || 0;
    if (!lesson.completed && resume > 5 && resume < v.duration - 5) v.currentTime = resume;
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    const now = Date.now();
    if (now - lastSentRef.current > PROGRESS_INTERVAL_MS) {
      lastSentRef.current = now;
      sendProgress(v.currentTime);
    }
  };

  const onPause = () => {
    const v = videoRef.current;
    if (v && !v.ended) sendProgress(v.currentTime);
  };

  const onEnded = () => {
    const v = videoRef.current;
    sendProgress(v ? v.duration : lesson?.duration_seconds || 0, true);
    setEndedLessonId(lesson?.id || null);
  };

  // Flush progress when leaving the page.
  useEffect(() => {
    const flush = () => {
      const v = videoRef.current;
      if (v && v.currentTime > 0 && !v.ended) sendProgress(v.currentTime);
    };
    window.addEventListener("beforeunload", flush);
    return () => { flush(); window.removeEventListener("beforeunload", flush); };
  }, [sendProgress]);

  // Auto-advance countdown.
  useEffect(() => {
    if (!ended || !nextLesson) return undefined;
    if (countdown <= 0) {
      navigate(`/learn/${slug}/watch/${nextLesson.position}`);
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [ended, countdown, nextLesson, navigate, slug]);

  const onNoteChange = (value) => {
    setNote(value);
    setNoteState("saving");
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(async () => {
      try {
        await saveNote(lesson.id, value);
        patchLesson(lesson.id, { note: value });
        setNoteState("saved");
      } catch (_) { setNoteState("idle"); }
    }, 800);
  };

  const onToggleSave = async () => {
    if (!course) return;
    const prev = course.is_saved;
    setData((d) => ({ ...d, course: { ...d.course, is_saved: !prev } }));
    try { await toggleSave(course.id); }
    catch (_) { setData((d) => ({ ...d, course: { ...d.course, is_saved: prev } })); }
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: `${course.title} · ${lesson.title}`, url }); return; }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {}
  };

  const markComplete = () => {
    sendProgress(lesson?.duration_seconds || 0, true);
    setEndedLessonId(lesson?.id || null);
  };

  if (error) {
    return (
      <div className="learn-container">
        <div className="learn-empty learn-empty--dark">
          <h3>{error === "not_found" ? "We couldn't find that lesson." : "Something went wrong."}</h3>
          <Link to="/learn" className="learn-btn learn-btn--primary">Back to the hub</Link>
        </div>
      </div>
    );
  }

  const resources = [
    ...((lesson?.resources && Array.isArray(lesson.resources)) ? lesson.resources : []),
    ...(course?.guide_url ? [{ label: "Class guide (PDF)", url: course.guide_url }] : []),
  ];

  return (
    <>
      {course && lesson && !lesson.locked && (
        <>
          <div className="learn-container learn-watch">
            <div className="learn-watch__topbar">
              <Link to={`/learn/${course.slug}`} className="learn-back learn-back--light">← {course.title}</Link>
              <span className="learn-watch__counter">{pos} / {lessons.length}</span>
            </div>

            <div className="learn-watch__layout">
              <div className="learn-watch__main">
                <div
                  className={cn("learn-player", { "learn-player--protected": isPackage })}
                  onContextMenu={isPackage ? (e) => e.preventDefault() : undefined}
                >
                  {playbackSrc ? (
                    <>
                      <video
                        key={lesson.id}
                        ref={videoRef}
                        src={playbackSrc}
                        poster={lesson.poster_url || course.cover_image_url || undefined}
                        controls
                        controlsList={isPackage ? "nodownload noremoteplayback" : undefined}
                        disablePictureInPicture={isPackage || undefined}
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={onLoadedMetadata}
                        onTimeUpdate={onTimeUpdate}
                        onPause={onPause}
                        onEnded={onEnded}
                      />
                      {isPackage && access.email && (
                        <span className="learn-player__watermark" aria-hidden="true">{access.email}</span>
                      )}
                    </>
                  ) : hasVideo ? (
                    <div className="learn-player__placeholder">
                      {(lesson.poster_url || course.cover_image_url) && <img src={lesson.poster_url || course.cover_image_url} alt="" />}
                      <div className="learn-player__placeholder-copy">
                        <div className="learn-eyebrow learn-eyebrow--light">Locked</div>
                        <p>This session is part of a Summit package.</p>
                        <Link to={`/learn/${course.slug}`} className="learn-btn learn-btn--primary">Unlock</Link>
                      </div>
                    </div>
                  ) : (
                    <div className="learn-player__placeholder">
                      {(lesson.poster_url || course.cover_image_url) && <img src={lesson.poster_url || course.cover_image_url} alt="" />}
                      <div className="learn-player__placeholder-copy">
                        <div className="learn-eyebrow learn-eyebrow--light">Video coming soon</div>
                        <p>{isPackage ? "This session is being edited and will appear here as soon as it's ready." : "This lesson is being filmed. Read the outline below and mark it done when you're ready."}</p>
                        {!lesson.completed && !isPackage && (
                          <button type="button" className="learn-btn learn-btn--primary" onClick={markComplete}>Mark as complete</button>
                        )}
                      </div>
                    </div>
                  )}

                  {ended && (
                    <div className="learn-player__overlay">
                      <div className="learn-eyebrow learn-eyebrow--light">Lesson complete</div>
                      {nextLesson ? (
                        <>
                          <h3>Up next · {nextLesson.position}. {nextLesson.title}</h3>
                          <p>Starting in {countdown}s</p>
                          <div className="learn-player__overlay-actions">
                            <Link to={`/learn/${course.slug}/watch/${nextLesson.position}`} className="learn-btn learn-btn--primary">Play next</Link>
                            <button type="button" className="learn-btn learn-btn--ghost" onClick={() => { setEndedLessonId(null); setCountdown(AUTO_ADVANCE_SECONDS); }}>Stay here</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <h3>You finished {course.title}.</h3>
                          <div className="learn-player__overlay-actions">
                            <Link to="/learn" className="learn-btn learn-btn--primary">Back to the hub</Link>
                            <button type="button" className="learn-btn learn-btn--ghost" onClick={() => setEndedLessonId(null)}>Stay here</button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="learn-watch__meta">
                  <div className="learn-watch__meta-copy">
                    <h1 className="learn-watch__title">
                      <span className="learn-lesson__num">{lesson.position}.</span> {lesson.title}
                    </h1>
                    {lesson.description && <p className="learn-watch__desc">{lesson.description}</p>}
                  </div>
                  <div className="learn-watch__meta-actions">
                    <button type="button" className="learn-btn learn-btn--icon" onClick={onShare}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 10V2M5 5l3-3 3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 9v4h10V9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      <span>{copied ? "Link copied" : "Share"}</span>
                    </button>
                    {course.guide_url && (
                      <a href={course.guide_url} target="_blank" rel="noopener noreferrer" className="learn-btn learn-btn--icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 2h5l3 3v9H4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M9 2v3h3" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>
                        <span>Class guide</span>
                      </a>
                    )}
                    <button type="button" className={cn("learn-btn learn-btn--icon", { "learn-btn--saved": course.is_saved })} onClick={onToggleSave} aria-pressed={!!course.is_saved}>
                      {course.is_saved ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 2h8v12l-4-3-4 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>
                      )}
                      <span>{course.is_saved ? "Saved" : "Save"}</span>
                    </button>
                  </div>
                </div>

                <div className="learn-watch__pager">
                  {prevLesson ? (
                    <Link to={`/learn/${course.slug}/watch/${prevLesson.position}`} className="learn-pager">
                      <span className="learn-eyebrow learn-eyebrow--light">Previous</span>
                      <span>{prevLesson.position}. {prevLesson.title}</span>
                    </Link>
                  ) : <span />}
                  {nextLesson && (
                    <Link to={`/learn/${course.slug}/watch/${nextLesson.position}`} className="learn-pager learn-pager--next">
                      <span className="learn-eyebrow learn-eyebrow--light">Up next</span>
                      <span>{nextLesson.position}. {nextLesson.title}</span>
                    </Link>
                  )}
                </div>
              </div>

              <aside className="learn-rail">
                <Link to={`/learn/${course.slug}`} className="learn-rail__course">
                  {course.instructor_avatar_url && <img src={course.instructor_avatar_url} alt="" />}
                  <div>
                    <div className="learn-rail__course-title">{course.title}</div>
                    <div className="learn-rail__course-sub">
                      {course.instructor_name ? `${course.instructor_name} · ` : ""}{countLabel(course)}
                    </div>
                  </div>
                </Link>

                <div className="learn-tabs" role="tablist">
                  {[["lessons", "All lessons"], ["notes", "My notes"], ["resources", "Resources"]].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={tab === key}
                      className={cn("learn-tab", { "learn-tab--active": tab === key })}
                      onClick={() => setTab(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {tab === "lessons" && (
                  <ol className="learn-rail__list">
                    {lessons.map((l) => {
                      const current = l.id === lesson.id;
                      return (
                        <li key={l.id}>
                          <Link
                            to={`/learn/${course.slug}/watch/${l.position}`}
                            className={cn("learn-rail__item", { "learn-rail__item--current": current, "learn-rail__item--done": l.completed })}
                            aria-current={current ? "true" : undefined}
                          >
                            <div className="learn-rail__thumb">
                              {l.poster_url ? <img src={l.poster_url} alt="" loading="lazy" /> : <div className="learn-card__media-fallback" />}
                              {current ? (
                                <span className="learn-rail__playing" aria-hidden="true">
                                  <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor"><path d="M3 1.5v11l9-5.5z"/></svg>
                                </span>
                              ) : (
                                <span className="learn-rail__time">{formatClock(l.duration_seconds)}</span>
                              )}
                            </div>
                            <div className="learn-rail__text">
                              <span className="learn-rail__name">{l.position}. {l.title}</span>
                              {l.completed && <span className="learn-rail__done">Completed</span>}
                              {!l.completed && l.seconds_watched > 0 && l.duration_seconds > 0 && (
                                <span className="learn-progress learn-progress--rail">
                                  <span className="learn-progress__bar" style={{ width: `${Math.min(100, Math.round((l.seconds_watched / l.duration_seconds) * 100))}%` }} />
                                </span>
                              )}
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ol>
                )}

                {tab === "notes" && (
                  <div className="learn-notes">
                    <textarea
                      value={note}
                      onChange={(e) => onNoteChange(e.target.value)}
                      placeholder="Type your notes for this lesson here…"
                      rows={12}
                    />
                    <div className="learn-notes__status">
                      {noteState === "saving" ? "Saving…" : noteState === "saved" ? "Saved" : "Private to you"}
                    </div>
                  </div>
                )}

                {tab === "resources" && (
                  <div className="learn-resources">
                    {resources.length === 0 ? (
                      <p className="learn-resources__empty">No downloads for this lesson.</p>
                    ) : (
                      <ul>
                        {resources.map((r, i) => (
                          <li key={`${r.url}-${i}`}>
                            <a href={r.url} target="_blank" rel="noopener noreferrer">
                              <span>{r.label || r.url}</span>
                              <span aria-hidden="true">↗</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="learn-resources__meta">
                      {lessons.length} lessons · {formatDuration(course.total_seconds)}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </div>

          <div className="learn-container">
            <CourseRow title="Up next in the hub" courses={data.related} seeAllTo="/learn/library" packages={access.packages} isAttendee={access.isAttendee} />
          </div>
        </>
      )}
    </>
  );
};

const LearnWatch = () => (
  <LearnShell dark className="learn-watch-page" redirectWhenComingSoon>
    <WatchBody />
  </LearnShell>
);

export default LearnWatch;
